import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { mollieService } from 'receivable-invoices-js/react/services/mollie';
import { useSentry } from 'receivable-invoices-js/react/hooks/use-sentry';

interface MollieComponentInstance {
  mount: (element: HTMLElement | string) => void;
  unmount: () => void;
  addEventListener: (event: string, callback: (event: MollieEvent) => void) => void;
}

interface MollieEvent {
  error?: string;
  touched?: boolean;
  dirty?: boolean;
}

interface MollieStyle {
  styles: {
    base: Record<string, string>;
  };
}

// Discrimiated union type ensures that we only need to check isLoaded to use the service methods
type MollieHookReturn =
  | {
      isLoaded: false;
      load: (profileId: string) => Promise<void>;
      hasError: boolean;
      createComponent: null;
      createToken: null;
    }
  | {
      isLoaded: true;
      load: (profileId: string) => Promise<void>;
      hasError: boolean;
      createComponent: (id: string, style?: MollieStyle) => MollieComponentInstance;
      createToken: () => Promise<{ token: string; error: string }>;
    };

/**
 * React custom hook that provides a reactive interface to the Mollie service.
 * Subscribes to service state changes and exposes type-safe methods for creating
 * payment components and tokens.
 */
export const useMollie = (): MollieHookReturn => {
  const { locale } = useIntl();
  const sentry = useSentry();

  const [isLoaded, setIsLoaded] = useState(mollieService.isLoaded);
  const [hasError, setHasError] = useState(mollieService.hasError);

  useEffect(() => {
    const unsubscribe = mollieService.subscribe(() => {
      setIsLoaded(mollieService.isLoaded);
      setHasError(mollieService.hasError);
    });

    return unsubscribe;
  }, []);

  const load = useCallback(
    async (profileId: string) => {
      await mollieService.load(profileId, locale, error => {
        sentry.captureException(error);
      });
    },
    [locale, sentry]
  );

  // Ensure all methods are available before returning the service
  if (isLoaded && mollieService.createComponent && mollieService.createToken) {
    return {
      isLoaded: true,
      load,
      hasError,
      createComponent: mollieService.createComponent,
      createToken: mollieService.createToken,
    };
  }

  return {
    isLoaded: false,
    load,
    hasError,
    createComponent: null,
    createToken: null,
  };
};
